"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Lenis from "lenis";

let lenisInstance: Lenis | null = null;

export function getLenis() {
  return lenisInstance;
}

export default function SmoothScroll() {
  const pathname = usePathname();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisInstance = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);

  // Scroll to a hash target
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    // Retry scrolling until the target element position stabilizes
    let attempts = 0;
    let lastTop = -1;
    let settled = false;
    let rafId: number;

    function tryScroll() {
      const target = document.querySelector(hash) as HTMLElement | null;
      if (!target || settled) return;

      const rect = target.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;

      attempts++;

      // If element position has stabilized or we've tried enough times
      if ((Math.abs(absoluteTop - lastTop) < 2 && attempts > 3) || attempts > 15) {
        settled = true;
        const lenis = lenisInstance;
        if (lenis) {
          lenis.scrollTo(target, { immediate: true, offset: -80 });
        } else {
          target.scrollIntoView({ block: "start" });
        }
        return;
      }

      lastTop = absoluteTop;
      setTimeout(() => { rafId = requestAnimationFrame(tryScroll); }, 100);
    }

    // Start after initial render
    const timeout = setTimeout(() => {
      rafId = requestAnimationFrame(tryScroll);
    }, 200);

    // Handle hash changes on same page (smooth scroll)
    function onHashChange() {
      const newHash = window.location.hash;
      if (!newHash) return;
      const target = document.querySelector(newHash) as HTMLElement | null;
      if (!target) return;
      const lenis = lenisInstance;
      if (lenis) {
        lenis.scrollTo(target, { offset: -80, duration: 1.2 });
      } else {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    window.addEventListener("hashchange", onHashChange);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(rafId);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [pathname]);

  return null;
}
